function pruneDesktopOnlyFacetsOnMobile() {
  // Desktop filter/sort UI is duplicated for progressive enhancement and only ever
  // shown via the `.small-hide` CSS rule at >= 750px. On mobile it stays in the DOM
  // (and accessibility tree) unused, roughly doubling facet links/controls on large
  // collections. Pull it out before it upgrades into custom elements, and put it
  // back if the viewport grows past the breakpoint.
  const desktopOnlyFacets = Array.from(document.querySelectorAll('facet-filters-form.small-hide'));
  if (!desktopOnlyFacets.length) return;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const entries = desktopOnlyFacets.map((element) => ({
    element,
    placeholder: document.createComment('desktop-facets-placeholder'),
  }));

  const syncFacets = (isMobile) => {
    entries.forEach(({ element, placeholder }) => {
      if (isMobile && element.isConnected) {
        element.replaceWith(placeholder);
      } else if (!isMobile && placeholder.isConnected) {
        placeholder.replaceWith(element);
      }
    });
  };

  syncFacets(mobileQuery.matches);
  mobileQuery.addEventListener('change', (event) => syncFacets(event.matches));
}

pruneDesktopOnlyFacetsOnMobile();

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.onWidthRangeInput.bind(this));
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  // WIDTH-RANGE-HOTFIX (ITT-6370): a width band checkbox toggles the hidden custom.size inputs it groups.
  // Remove with snippets/width-range-bands.liquid once custom.width_band is a Search & Discovery filter.
  onWidthRangeInput(event) {
    if (!event.target.hasAttribute('data-width-range')) return;

    event.target
      .closest('.facets__item')
      .querySelectorAll('[data-width-range-value]')
      .forEach((input) => {
        if (!input.disabled) input.checked = event.target.checked;
      });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
    document.addEventListener('click', FacetFiltersForm.onPaginationClick);
  }

  static storageKey(pathname = window.location.pathname) {
    return `plp_filters:${pathname}`;
  }

  static isCollectionPage(pathname = window.location.pathname) {
    return pathname.includes('/collections/');
  }

  static stateParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    const state = new URLSearchParams();

    params.forEach((value, key) => {
      if (key === 'sort_by' || key.startsWith('filter.')) {
        state.append(key, value);
      }
    });

    return state.toString();
  }

  static isManagedParam(key) {
    return key === 'page' || key === 'sort_by' || key === 'q' || key === 'options[prefix]' || key.startsWith('filter.');
  }

  static cleanSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    params.delete('section_id');

    return params.toString();
  }

  static mergeSearchParams(searchParams, { resetManagedParams = false, preservePage = false } = {}) {
    const mergedParams = new URLSearchParams(window.location.search);
    const incomingParams = new URLSearchParams(searchParams);
    const incomingKeys = new Set();

    if (resetManagedParams) {
      Array.from(mergedParams.keys()).forEach((key) => {
        if (FacetFiltersForm.isManagedParam(key)) mergedParams.delete(key);
      });
    }

    incomingParams.forEach((value, key) => {
      if (key !== 'section_id') incomingKeys.add(key);
    });

    incomingKeys.forEach((key) => mergedParams.delete(key));
    incomingParams.forEach((value, key) => {
      if (key !== 'section_id') mergedParams.append(key, value);
    });

    if (!preservePage) mergedParams.delete('page');
    mergedParams.delete('section_id');

    return mergedParams.toString();
  }

  static sectionUrl(section, searchParams) {
    const params = new URLSearchParams(searchParams);
    params.set('section_id', section.section);

    return `${window.location.pathname}?${params.toString()}`;
  }

  static safeSessionStorage(callback) {
    try {
      return callback(window.sessionStorage);
    } catch (error) {
      return null;
    }
  }

  static updateStoredCollectionUrl(searchParams = '') {
    const path = window.location.pathname;
    if (!FacetFiltersForm.isCollectionPage(path)) return;

    FacetFiltersForm.safeSessionStorage((storage) => {
      storage.setItem('last_collection_url', `${path}${searchParams ? `?${searchParams}` : ''}`);
    });
  }

  static persistState(searchParams) {
    if (!FacetFiltersForm.isCollectionPage()) return;

    const state = FacetFiltersForm.stateParams(searchParams);

    FacetFiltersForm.safeSessionStorage((storage) => {
      if (state) {
        storage.setItem(FacetFiltersForm.storageKey(), state);
      } else {
        storage.removeItem(FacetFiltersForm.storageKey());
      }
    });

    FacetFiltersForm.updateStoredCollectionUrl(FacetFiltersForm.cleanSearchParams(searchParams));
  }

  static restoreState() {
    if (!FacetFiltersForm.isCollectionPage()) return;

    const currentSearchParams = FacetFiltersForm.cleanSearchParams(window.location.search);
    const currentState = FacetFiltersForm.stateParams(currentSearchParams);

    if (currentState) {
      FacetFiltersForm.persistState(currentSearchParams);
      return;
    }

    const savedState = FacetFiltersForm.safeSessionStorage((storage) =>
      storage.getItem(FacetFiltersForm.storageKey())
    );

    if (!savedState) return;

    const savedSearchParams = FacetFiltersForm.mergeSearchParams(savedState, { resetManagedParams: true });
    history.replaceState({ searchParams: savedSearchParams }, '', `${window.location.pathname}?${savedSearchParams}`);
    FacetFiltersForm.searchParamsInitial = savedSearchParams;
    FacetFiltersForm.searchParamsPrev = savedSearchParams;
    FacetFiltersForm.updateStoredCollectionUrl(savedSearchParams);
    FacetFiltersForm.renderPage(savedSearchParams, null, false);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    searchParams = FacetFiltersForm.cleanSearchParams(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    const productGridContainer = document.getElementById('ProductGridContainer');
    const collectionContainer = productGridContainer ? productGridContainer.querySelector('.collection') : null;
    if (collectionContainer) collectionContainer.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const renderPromises = sections.map((section) => {
      const url = FacetFiltersForm.sectionUrl(section, searchParams);
      const filterDataUrl = (element) => element.url === url;

      return FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) {
      FacetFiltersForm.updateURLHash(searchParams);
      FacetFiltersForm.persistState(searchParams);
    }

    return Promise.all(renderPromises);
  }

  static renderSectionFromFetch(url, event) {
    return fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);

    return Promise.resolve();
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCountDesktop');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (count && containerDesktop) {
      containerDesktop.innerHTML = count.innerHTML;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    if (!document.querySelector('facet-filters-form') && !parsedHTML.querySelector('facet-filters-form')) return;

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const target = event && event.target instanceof Element ? event.target : null;
      const jsFilter = target ? target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        // The desktop facet wrapper is intentionally pruned from the DOM on mobile
        // (see pruneDesktopOnlyFacetsOnMobile), so its filters won't have a live
        // parent/sibling to anchor against here — skip re-inserting them.
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          const previousElement = document.getElementById(previousElementId);
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName && previousElement) {
            previousElement.after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          const parentInDom = document.getElementById(elementToRender.parentElement.id);
          const anchor = parentInDom ? parentInDom.querySelector('.js-filter') : null;
          if (anchor) anchor.before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      // '.active-facets-desktop' won't exist on mobile once the desktop wrapper is pruned.
      if (!activeFacetsElement || !target) return;
      target.innerHTML = activeFacetsElement.innerHTML;
    });


    const currentMobileActiveFacets = document.querySelectorAll('.mobile-facets__active-facets');
    currentMobileActiveFacets.forEach((el) => el.innerHTML = '');
    const newMobileActiveFacets = html.querySelectorAll('.mobile-facets__active-facets');
    newMobileActiveFacets.forEach((newElement) => {
      const targetElement = document.getElementById(newElement.id);
      if (targetElement)
        targetElement.innerHTML = newElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.mobile-facets__count-visible',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const target = document.querySelector(selector);
      // '.sorting' only exists inside the desktop-only wrapper for vertical filters,
      // which is pruned from the DOM on mobile.
      if (!sourceElement || !target) return;
      target.innerHTML = sourceElement.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) mobileForm.closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    searchParams = FacetFiltersForm.cleanSearchParams(searchParams);
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static onPaginationClick(event) {
    const target = event.target instanceof Element ? event.target : event.target.parentElement;
    const link = target ? target.closest('#ProductGridContainer .pagination a[href]') : null;
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return;

    event.preventDefault();

    const searchParams = FacetFiltersForm.mergeSearchParams(url.search, { preservePage: true });
    FacetFiltersForm.setPaginationLoading(link, true);
    FacetFiltersForm.scrollToProductGrid();
    FacetFiltersForm.renderPage(searchParams, null)
      .catch(() => FacetFiltersForm.setPaginationLoading(link, false));
  }

  static setPaginationLoading(link, isLoading) {
    const wrapper = link
      ? link.closest('.pagination-wrapper')
      : document.querySelector('#ProductGridContainer .pagination-wrapper.loading');

    if (!wrapper) return;

    const spinner = wrapper.querySelector('.loading__spinner');

    wrapper.classList.toggle('loading', isLoading);
    wrapper.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  static scrollToProductGrid() {
    const productsSection = document.querySelector('collection-product-grid') || document.getElementById('ProductGridContainer');
    if (!productsSection) return;

    const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height-mobile')) || 0;
    const announcementBar = document.querySelector('.announcement-bar-timer-section-wrapper');
    const announcementHeight = announcementBar ? announcementBar.getBoundingClientRect().height : 0;
    const facetWrapper = document.querySelector('.facets-wrapper');
    const facetWrapperHeight = facetWrapper ? facetWrapper.getBoundingClientRect().height : 0;
    const elementPosition = productsSection.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - headerHeight - announcementHeight - facetWrapperHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    searchParams = FacetFiltersForm.mergeSearchParams(searchParams, { resetManagedParams: true });
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const searchParams = FacetFiltersForm.mergeSearchParams(url, { resetManagedParams: true });
    FacetFiltersForm.renderPage(searchParams);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.restoreState();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.slider = this.querySelector('.price-range-slider');
    this.setMinAndMaxValues();
  }

  connectedCallback() {
    if (this.slider) {
      this.initSlider();
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    if (this.slider)
      this.cascadeUpdateSlider();
    this.cascadeUpdateSlider();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  initSlider() {
    const maxPrice = +this.slider.getAttribute('data-max-price').replaceAll(',', '');
    window.waitForMod('noUiSlider').then((noUiSlider) => {
      noUiSlider.create(this.slider, {
        start: [0, maxPrice],
        connect: true,
        range: {
          min: 0,
          max: maxPrice
        },
        step: 1,
        format: {
          to: value => Math.round(value),
          from: value => Number(value)
        }
      });

      this.slider.noUiSlider.on('change', this.cascadeUpdateInputs.bind(this));
    });
  }
  cascadeUpdateInputs() {
    const [min, max] = this.slider.noUiSlider.get();
    const minPriceEl = this.querySelector('input.price-range-min');
    const maxPriceEl = this.querySelector('input.price-range-max');
    if (minPriceEl.value != min) {
      minPriceEl.value = Math.round(min);
      minPriceEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (maxPriceEl.value != max) {
      maxPriceEl.value = Math.round(max);
      maxPriceEl.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }
  cascadeUpdateSlider() {
    const min = this.querySelector('.price-range-min').value
    const max = this.querySelector('.price-range-max').value
    this.slider.noUiSlider.set([min, max]);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
